import {
    createEntityAdapter,
    createSelector,
} from '@reduxjs/toolkit'

import {
    apiSlice,
} from '@/app/api/apiSlice'


/*
|--------------------------------------------------------------------------
| Adapter
|--------------------------------------------------------------------------
|
| MongoDB meeting records use _id rather than id.
|
| createEntityAdapter defaults to entity.id unless selectId is supplied.
|
| Therefore, explicitly use meeting._id as the normalized entity key.
|
*/

const trinityMeetingsAdapter =
    createEntityAdapter({

        selectId:
            meeting =>
                meeting._id,


        sortComparer:
            (
                a,
                b
            ) => {

                const aStart =
                    a?.meetingStart
                        ?
                        new Date(
                            a.meetingStart
                        ).getTime()
                        :
                        0


                const bStart =
                    b?.meetingStart
                        ?
                        new Date(
                            b.meetingStart
                        ).getTime()
                        :
                        0


                return bStart - aStart

            },

    })


/*
|--------------------------------------------------------------------------
| Initial State
|--------------------------------------------------------------------------
*/

const initialState =
    trinityMeetingsAdapter
        .getInitialState()


/*
|--------------------------------------------------------------------------
| API Slice
|--------------------------------------------------------------------------
*/

export const trinityMeetingsApiSlice =
    apiSlice.injectEndpoints({

        endpoints:
            builder => ({


                /*
                |--------------------------------------------------------------------------
                | Get Trinity Meetings
                |--------------------------------------------------------------------------
                */

                getTrinityMeetings:
                    builder.query({

                        query:
                            ({
                                orgId,
                                meetingStatus,
                                calendarCreationStatus,
                            } = {}) => {


                                const params =
                                    new URLSearchParams()


                                if (
                                    orgId
                                ) {

                                    params.append(
                                        'orgId',
                                        orgId
                                    )

                                }


                                if (
                                    meetingStatus
                                ) {

                                    params.append(
                                        'meetingStatus',
                                        meetingStatus
                                    )

                                }


                                if (
                                    calendarCreationStatus
                                ) {

                                    params.append(
                                        'calendarCreationStatus',
                                        calendarCreationStatus
                                    )

                                }


                                const queryString =
                                    params.toString()


                                return {

                                    url:
                                        queryString
                                            ?
                                            `/trinityMeetings/getTrinityMeetings?${queryString}`
                                            :
                                            '/trinityMeetings/getTrinityMeetings',

                                    method:
                                        'GET',

                                }

                            },


                        /*
                        |--------------------------------------------------------------------------
                        | Normalize Response
                        |--------------------------------------------------------------------------
                        |
                        | Preserve populated participantUserIds exactly as returned by the backend.
                        |
                        */

                        transformResponse:
                            responseData => {

                                const meetings =
                                    Array.isArray(
                                        responseData
                                    )
                                        ?
                                        responseData
                                            .filter(
                                                meeting =>
                                                    meeting
                                                    &&
                                                    meeting._id
                                            )
                                            .map(
                                                meeting => ({

                                                    ...meeting,

                                                    participantUserIds:
                                                        Array.isArray(
                                                            meeting
                                                                .participantUserIds
                                                        )
                                                            ?
                                                            meeting
                                                                .participantUserIds
                                                                .map(
                                                                    participant => {

                                                                        if (
                                                                            !participant
                                                                        ) {

                                                                            return participant

                                                                        }


                                                                        if (
                                                                            typeof participant ===
                                                                            'string'
                                                                        ) {

                                                                            return participant

                                                                        }


                                                                        return {

                                                                            ...participant,

                                                                            _id:
                                                                                participant
                                                                                    ._id,

                                                                            name:
                                                                                participant
                                                                                    .name
                                                                                ||
                                                                                '',

                                                                            email:
                                                                                participant
                                                                                    .email
                                                                                ||
                                                                                '',

                                                                        }

                                                                    }
                                                                )
                                                            :
                                                            [],

                                                }))
                                        :
                                        []


                                return trinityMeetingsAdapter
                                    .setAll(
                                        initialState,
                                        meetings
                                    )

                            },


                        providesTags:
                            result => {

                                if (
                                    !result?.ids
                                ) {

                                    return [

                                        {
                                            type:
                                                'TrinityMeeting',

                                            id:
                                                'LIST',
                                        },

                                    ]

                                }


                                return [

                                    {
                                        type:
                                            'TrinityMeeting',

                                        id:
                                            'LIST',
                                    },

                                    ...result.ids.map(
                                        id => ({

                                            type:
                                                'TrinityMeeting',

                                            id,

                                        })
                                    ),

                                ]

                            },

                    }),


                /*
                |--------------------------------------------------------------------------
                | Get Trinity Meeting By ID
                |--------------------------------------------------------------------------
                */

                getTrinityMeetingById:
                    builder.query({

                        query:
                            meetingId => ({

                                url:
                                    '/trinityMeetings/getTrinityMeetingById',

                                method:
                                    'GET',

                                params: {

                                    meetingId,

                                },

                            }),


                        providesTags:
                            (
                                result,
                                error,
                                meetingId
                            ) => [

                                {
                                    type:
                                        'TrinityMeeting',

                                    id:
                                        meetingId,

                                },

                            ],

                    }),


                /*
                |--------------------------------------------------------------------------
                | Create New Trinity Meeting
                |--------------------------------------------------------------------------
                |
                | JSON body.
                |
                */

                createNewTrinityMeeting:
                    builder.mutation({

                        query:
                            meetingData => ({

                                url:
                                    '/trinityMeetings/createNewTrinityMeeting',

                                method:
                                    'POST',

                                body:
                                    meetingData,

                            }),


                        invalidatesTags: [

                            {
                                type:
                                    'TrinityMeeting',

                                id:
                                    'LIST',

                            },

                        ],

                    }),

            }),

    })


/*
|--------------------------------------------------------------------------
| Hooks
|--------------------------------------------------------------------------
*/

export const {

    useGetTrinityMeetingsQuery,

    useGetTrinityMeetingByIdQuery,

    useCreateNewTrinityMeetingMutation,

} =
    trinityMeetingsApiSlice


/*
|--------------------------------------------------------------------------
| Query Result Selector
|--------------------------------------------------------------------------
*/

const selectTrinityMeetingsResult =
    trinityMeetingsApiSlice
        .endpoints
        .getTrinityMeetings
        .select()


/*
|--------------------------------------------------------------------------
| Normalized Data Selector
|--------------------------------------------------------------------------
*/

const selectTrinityMeetingsData =
    createSelector(

        selectTrinityMeetingsResult,

        trinityMeetingsResult =>
            trinityMeetingsResult?.data
            ??
            initialState

    )


/*
|--------------------------------------------------------------------------
| Adapter Selectors
|--------------------------------------------------------------------------
*/

export const {

    selectAll:
        selectAllTrinityMeetings,

    selectById:
        selectTrinityMeetingById,

    selectIds:
        selectTrinityMeetingIds,

} =
    trinityMeetingsAdapter
        .getSelectors(

            state =>
                selectTrinityMeetingsData(
                    state
                )

        )